import type { TranslationOptions } from '@topolo-io/ui-kit';
import { createLiveCopy } from './live-copy';
import { createCrmAdvancedFiltersCopy } from './crm-advanced-filters-copy';
import { createCrmCompaniesCopy } from './crm-companies-copy';
import { createCrmCompanyDetailsCopy } from './crm-company-details-copy';
import { createCrmContactDetailsCopy } from './crm-contact-details-copy';
import { createCrmContactFormCopy } from './crm-contact-form-copy';
import { createCrmContactListCopy } from './crm-contact-list-copy';
import { createCrmCustomFieldsCopy } from './crm-custom-fields-copy';
import { createCrmCustomObjectsCopy } from './crm-custom-objects-copy';
import { createCrmDashboardCopy } from './crm-dashboard-copy';
import { createCrmDealsCopy } from './crm-deals-copy';
import { createCrmGettingStartedCopy } from './crm-getting-started-copy';
import { createCrmImportCopy } from './crm-import-copy';
import { createCrmPacksCopy } from './crm-packs-copy';
import { createCrmPipelineCopy } from './crm-pipeline-copy';
import { createCrmPortalCopy } from './crm-portal-copy';
import { createCrmRealEstateFormsCopy } from './crm-real-estate-forms-copy';
import { createCrmRealEstateCopy } from './crm-real-estate-copy';
import { createCrmRecordViewsCopy } from './crm-record-views-copy';
import { createCrmRuntimeCopy } from './crm-runtime-copy';
import { createCrmSettingsCopy } from './crm-settings-copy';
import { createCrmSharedUiCopy } from './crm-shared-ui-copy';
import { createCrmShellCopy } from './crm-shell-copy';
import { createCrmWorkflowCopy } from './crm-workflow-copy';

/** The translator the copy factories read through: ui-kit's `t`, key-only, with interpolation values. */
export type CrmTranslate = (key: string, options?: TranslationOptions) => string;

export function createCrmCopy(t: CrmTranslate) {
  return {
    ...createCrmShellCopy(t),
    ...createCrmDashboardCopy(t),
    ...createCrmSharedUiCopy(t),
    ...createCrmRuntimeCopy(t),
    ...createCrmGettingStartedCopy(t),
    ...createCrmContactListCopy(t),
    ...createCrmPipelineCopy(t),
    ...createCrmCompaniesCopy(t),
    ...createCrmContactDetailsCopy(t),
    ...createCrmRealEstateFormsCopy(t),
    ...createCrmPortalCopy(t),
    ...createCrmSettingsCopy(t),
    ...createCrmAdvancedFiltersCopy(t),
    ...createCrmWorkflowCopy(t),
    ...createCrmDealsCopy(t),
    ...createCrmRecordViewsCopy(t),
    ...createCrmRealEstateCopy(t),
    ...createCrmCompanyDetailsCopy(t),
    ...createCrmImportCopy(t),
    ...createCrmContactFormCopy(t),
    ...createCrmCustomFieldsCopy(t),
    ...createCrmCustomObjectsCopy(t),
    ...createCrmPacksCopy(t),
  };
}

export type CrmCopy = ReturnType<typeof createCrmCopy>;

const liveCrmCopy = createLiveCopy(createCrmCopy, 'CRM_COPY');

/**
 * CRM's copy for the active locale. Module-level handles (`const COPY = CRM_COPY.dealForm`) stay valid across
 * locales; text is read from the copy bound by `CrmCopyBoundary` when it is used.
 */
export const CRM_COPY: CrmCopy = liveCrmCopy.copy;

/** Binds the CRM copy to the active translator. `CrmCopyBoundary` calls it; tests bind the English inventory. */
export const bindCrmCopy = (t: CrmTranslate): CrmCopy => liveCrmCopy.bind(t);

/** Clears the binding, so reading copy fails loudly until a translator is bound again. */
export const unbindCrmCopy = (): void => liveCrmCopy.unbind();

export const isCrmCopyBound = (): boolean => liveCrmCopy.isBound();
